import { AUTH_ROLES } from "@/constants/auth";
import { graphqlRequest } from "@/graphql/client";
import { removeProperty } from "@/graphql/mutations";
import { onPropertyRemovalResponse } from "@/graphql/subscriptions";
import { stripPhotoThumbnailsFromRecords } from "@/pages/PropertyDetail/utils/propertyPhotoRecords";
import {
  batchGetProperties,
  fetchProperties,
  getCampaign,
  getPropComps,
  getPublicProperty,
  getSchema,
  updateProperty,
} from "@/graphql/queries";
import { decompressResponse } from "@/utils/decompressionUtils";
import { IS_DEV } from "@/utils/env";
import { isStatusCode, roleUsesServerTextSearch, validatePagination } from "./propertyCacheUtils";
import { sanitizePropertyIdForBatch } from "../dealCore/dealPropertyUtils";
import { parseGraphqlBody } from "../subscriptionCore/subscriptionApi";

/** Hats allowed on fetchProperties `user_role` (never superadmin). */
const FETCH_PROPERTIES_USER_ROLES = new Set(
  Object.values(AUTH_ROLES).filter((role) => role !== AUTH_ROLES.SUPERADMIN)
);

/**
 * Normalize and allowlist a list hat for GraphQL `user_role`.
 * @param {unknown} role
 * @returns {string} trimmed allowlisted role, or "" to omit
 */
function normalizeFetchPropertiesUserRole(role) {
  const trimmed = typeof role === "string" ? role.trim().toLowerCase() : "";
  if (!trimmed || !FETCH_PROPERTIES_USER_ROLES.has(trimmed)) return "";
  return trimmed;
}

/**
 * Resolves the FE list identity contract for fetchProperties calls.
 * Superadmin portfolio mode omits user scope; role-mode and impersonation mode
 * always scope by effective user id + active hat role.
 *
 * @param {{ cognitoIsSuperAdmin?: boolean, isImpersonating?: boolean, userId?: unknown, role?: unknown }} [params]
 * @returns {{ userId: string|null, role: string|null }}
 */
export function resolveFetchPropertiesListScope({
  cognitoIsSuperAdmin = false,
  isImpersonating = false,
  userId = null,
  role = null,
} = {}) {
  if (cognitoIsSuperAdmin && !isImpersonating) {
    return { userId: null, role: null };
  }

  const normalizedUserId = typeof userId === "string" ? userId.trim() : "";
  const normalizedRole = typeof role === "string" ? role.trim().toLowerCase() : "";

  return {
    userId: normalizedUserId || null,
    role: normalizedRole || null,
  };
}

/**
 * Checks whether API status code indicates success.
 *
 * @param {string|number|undefined|null} statusCode - Status code from API response
 * @returns {boolean} True when status is HTTP 200
 */
export const isSuccessStatusCode = (statusCode) => statusCode === 200 || statusCode === "200";

export function extractBodyString(apiBody) {
  if (typeof apiBody !== "string") {
    throw new Error("Invalid API body: expected string");
  }

  const trimmed = apiBody.trim();
  if (trimmed && (trimmed[0] === "{" || trimmed[0] === "[")) return apiBody;

  if (trimmed && trimmed[0] === '"') {
    try {
      const unwrapped = JSON.parse(trimmed);
      if (typeof unwrapped === "string") {
        const inner = unwrapped.trim();
        if (inner && (inner[0] === "{" || inner[0] === "[")) return inner;
      }
    } catch {
      // Fall through to decompression attempt.
    }
  }

  try {
    const decompressed = decompressResponse(apiBody);
    if (!decompressed) return apiBody;

    const decompressedTrimmed = decompressed.trim();
    if (decompressedTrimmed && decompressedTrimmed[0] === '"') {
      try {
        const unwrapped = JSON.parse(decompressedTrimmed);
        if (typeof unwrapped === "string") {
          const inner = unwrapped.trim();
          if (inner && (inner[0] === "{" || inner[0] === "[")) return inner;
        }
      } catch {
        // Ignore and return decompressed as-is.
      }
    }

    return decompressed;
  } catch {
    return apiBody;
  }
}

export function normalizeProperty(property, index = 0) {
  const uniqueId = property?.id ? `${property.id}` : `property_${Date.now()}_${index}`;
  const normalizedAttributes = {};
  let rawAdditionalAttributes = property?.additional_attributes;

  if (typeof rawAdditionalAttributes === "string") {
    try {
      const parsedAttributes = JSON.parse(rawAdditionalAttributes);
      if (parsedAttributes && typeof parsedAttributes === "object") {
        rawAdditionalAttributes = parsedAttributes;
      }
    } catch {
      rawAdditionalAttributes = {};
    }
  }

  if (rawAdditionalAttributes && typeof rawAdditionalAttributes === "object") {
    Object.entries(rawAdditionalAttributes).forEach(([key, value]) => {
      if (!key || value === undefined) return;
      const normalizedKey = String(key).trim();
      if (normalizedKey) normalizedAttributes[normalizedKey] = value;
    });
  }

  return {
    ...property,
    id: uniqueId,
    image: property?.metadata?.image || "/property-placeholder.png",
    additional_attributes: normalizedAttributes,
  };
}

function parseFetchPropertiesBody(apiResult) {
  const bodyString = extractBodyString(apiResult?.body || "");
  let parsed;

  try {
    parsed = JSON.parse(bodyString || "{}");
    if (typeof parsed === "string") {
      const inner = parsed.trim();
      if (inner && (inner[0] === "{" || inner[0] === "[")) {
        parsed = JSON.parse(inner);
      }
    }
  } catch (e) {
    if (typeof console !== "undefined" && console.error) {
      console.error("[propertyApi] Failed to parse fetchProperties body", {
        statusCode: apiResult?.statusCode,
        bodyLength: typeof bodyString === "string" ? bodyString.length : null,
      });
    }
    throw e;
  }

  if (parsed && isStatusCode(apiResult?.statusCode, 204)) {
    return { data: [], ids: {}, pagination: null, statusCode: 204, isEmpty: true };
  }

  if (!parsed?.data || !Array.isArray(parsed.data)) {
    throw new Error("Invalid response format: missing or invalid data field");
  }

  return {
    data: parsed.data.map((p, idx) => normalizeProperty(p, idx)),
    ids: parsed?.ids && Object.keys(parsed.ids).length > 0 ? parsed.ids : {},
    pagination: validatePagination(parsed?.pagination),
    statusCode: apiResult?.statusCode,
    isEmpty: false,
  };
}

/**
 * Fetches properties via AppSync `fetchProperties`.
 * GraphQL variables: `customer`, `property_ids`, `limit`, optional `next_page`,
 * optional `filters` (`text_search_query` when non-empty after trim and role allows server search),
 * optional `user_id` (HAI-766 act-as: Dynamo user hash), and optional allowlisted `user_role` (viewed hat).
 *
 * @param {{ customer?: string, nextPage?: unknown, propertyIds?: string[], limit?: number, role?: unknown, userId?: string|null, textSearchQuery?: string }} [params]
 */
export async function fetchPropertiesFromApi({
  customer,
  nextPage = null,
  propertyIds = [],
  limit = 100,
  role = null,
  userId = null,
  textSearchQuery,
} = {}) {
  const variables = {
    customer,
    property_ids: propertyIds,
    limit,
  };

  if (nextPage !== null && nextPage !== undefined) {
    variables.next_page = typeof nextPage === "string" ? nextPage : JSON.stringify(nextPage);
  }

  const trimmedSearch = String(textSearchQuery ?? "").trim();
  const roleKnown = role != null && String(role).trim() !== "";
  if (trimmedSearch && (!roleKnown || roleUsesServerTextSearch(role))) {
    variables.filters = JSON.stringify({ text_search_query: trimmedSearch });
  }

  const actingUserId = typeof userId === "string" ? userId.trim() : "";
  if (actingUserId) {
    variables.user_id = actingUserId;
  }

  const actingUserRole = normalizeFetchPropertiesUserRole(role);
  if (actingUserRole) {
    variables.user_role = actingUserRole;
  }

  if (IS_DEV) {
    console.log("[propertyApi][debug] fetchProperties variables", {
      context: {
        role: role || "unknown",
        userId: userId || "unknown",
        customer: customer || "unknown",
      },
      variables: {
        customer: variables.customer,
        property_ids: variables.property_ids,
        next_page: variables.next_page ?? null,
        limit: variables.limit,
        filters: variables.filters ?? null,
        user_id: variables.user_id ?? null,
        user_role: variables.user_role ?? null,
      },
    });
  }

  const response = await graphqlRequest({
    query: fetchProperties,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  return parseFetchPropertiesBody(response?.data?.fetchProperties);
}

export const PUBLIC_PROPERTY_VIEW_REFRESH_TOP_LEVEL_KEYS = [
  "full_address",
  "city",
  "state",
  "zipcode",
  "county",
  "hidden_address",
  "active",
  "activation_state",
];

export const PUBLIC_PROPERTY_VIEW_REFRESH_ATTR_KEYS = [
  "photos",
  "video_link",
  "virtual_tour_link",
  "bedrooms",
  "bathrooms",
  "square_feet",
  "property_type",
  "distribution",
  // Comps report + QMA artifacts are property-level; carry them through so DealLens re-hydrates them.
  "comps_analysis",
  "comps_html_s3_key",
  "comps_pdf_s3_key",
  "qma_s3_key",
  "qma_pdf_s3_key",
  "pdf_s3_key",
  "estimated_price_low",
  "estimated_price_high",
  "estimated_price_avg",
];

function pickDefinedFields(source, keys) {
  if (!source || typeof source !== "object") return {};
  const out = {};
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Pulls DealLens view refresh fields from a getPublicProperty parsed body.
 *
 * @param {unknown} body
 * @returns {{ full_address?: string, city?: string, state?: string, zipcode?: string, county?: string, hidden_address?: boolean, additional_attributes?: object }|null}
 */
export function pickViewRefreshFields(body) {
  const data = body?.data;
  if (!data || typeof data !== "object") return null;

  const topLevel = pickDefinedFields(data, PUBLIC_PROPERTY_VIEW_REFRESH_TOP_LEVEL_KEYS);
  const additionalAttributes = pickDefinedFields(data.additional_attributes, PUBLIC_PROPERTY_VIEW_REFRESH_ATTR_KEYS);

  if (Object.keys(topLevel).length === 0 && Object.keys(additionalAttributes).length === 0) {
    return null;
  }

  return {
    ...topLevel,
    ...(Object.keys(additionalAttributes).length > 0 ? { additional_attributes: additionalAttributes } : {}),
  };
}

/**
 * Overlays live view refresh fields onto a MAOC snapshot property.
 *
 * @param {object|null|undefined} snapshotProperty
 * @param {{ full_address?: string, city?: string, state?: string, zipcode?: string, county?: string, hidden_address?: boolean, additional_attributes?: object }|null|undefined} refreshFields
 * @returns {object|null}
 */
export function applyViewRefreshFieldsToProperty(snapshotProperty, refreshFields) {
  if (!snapshotProperty || typeof snapshotProperty !== "object") return snapshotProperty || null;
  if (!refreshFields || typeof refreshFields !== "object") return snapshotProperty;

  const merged = {
    ...snapshotProperty,
    additional_attributes: {
      ...(snapshotProperty.additional_attributes && typeof snapshotProperty.additional_attributes === "object"
        ? snapshotProperty.additional_attributes
        : {}),
    },
  };

  for (const key of PUBLIC_PROPERTY_VIEW_REFRESH_TOP_LEVEL_KEYS) {
    if (refreshFields[key] !== undefined) {
      merged[key] = refreshFields[key];
    }
  }

  if (merged.hidden_address) {
    delete merged.full_address;
  }

  const refreshAttrs = refreshFields.additional_attributes;
  if (refreshAttrs && typeof refreshAttrs === "object") {
    for (const key of PUBLIC_PROPERTY_VIEW_REFRESH_ATTR_KEYS) {
      if (refreshAttrs[key] !== undefined) {
        merged.additional_attributes[key] = refreshAttrs[key];
      }
    }
    if (refreshAttrs.photos !== undefined) {
      merged.photos = refreshAttrs.photos;
    }
  }

  return merged;
}

/**
 * Overlays live public-property refresh fields onto a snapshot when the fetch succeeded.
 *
 * @param {object|null|undefined} snapshotProperty
 * @param {{ statusCode?: string|number, body?: unknown }|null|undefined} publicPropertyResult
 * @returns {object|null}
 */
export function mergePublicPropertyRefreshIntoSnapshot(snapshotProperty, publicPropertyResult) {
  if (!snapshotProperty || !publicPropertyResult || !isSuccessStatusCode(publicPropertyResult.statusCode)) {
    return snapshotProperty ?? null;
  }
  const refreshFields = pickViewRefreshFields(publicPropertyResult.body);
  if (!refreshFields) return snapshotProperty;
  return applyViewRefreshFieldsToProperty(snapshotProperty, refreshFields);
}

/**
 * Fetches public property data and merges view-refresh fields into a snapshot.
 * Returns the original snapshot when fetch/merge fails.
 *
 * @param {{ customer: string, propertyId: string, snapshotProperty: object, authMode?: string }} params
 * @returns {Promise<object|null>}
 */
export async function refreshSnapshotPropertyFromPublicApi({
  customer,
  propertyId,
  snapshotProperty,
  authMode = "API_KEY",
} = {}) {
  if (!customer || !propertyId || !snapshotProperty) return snapshotProperty ?? null;
  try {
    const publicPropertyResult = await fetchPublicPropertyFromApi({ customer, propertyId, authMode });
    return mergePublicPropertyRefreshIntoSnapshot(snapshotProperty, publicPropertyResult);
  } catch {
    return snapshotProperty;
  }
}

/**
 * Fetches a single property via the public AppSync resolver.
 *
 * @param {{ customer: string, propertyId: string, authMode?: string }} params
 * @returns {Promise<{ statusCode?: string|number, body: unknown, raw: object|null }>}
 */
export async function fetchPublicPropertyFromApi({ customer, propertyId, authMode = "API_KEY" } = {}) {
  const response = await graphqlRequest({
    query: getPublicProperty,
    variables: {
      customer,
      property_id: propertyId,
    },
    authMode,
  });

  const raw = response?.data?.getPublicProperty ?? null;
  return {
    statusCode: raw?.statusCode,
    body: parseGraphqlBody(raw?.body),
    raw,
  };
}

export async function fetchSchemaFromApi(customer) {
  const response = await graphqlRequest({
    query: getSchema,
    variables: { account: customer, entity: "property" },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResult = response?.data?.getSchema;
  if (!isStatusCode(apiResult?.statusCode, 200)) {
    throw new Error(`Schema fetch failed: ${apiResult?.statusCode}`);
  }

  const parsed = JSON.parse(apiResult?.body || "{}");
  if (Array.isArray(parsed?.structure)) {
    const hasIncludeOwners = parsed.structure.some((f) => f?.id === "include_owners");
    if (!hasIncludeOwners) {
      parsed.structure.push({
        id: "include_owners",
        type: "boolean",
        display_name: "Include Owners",
        description: "Include owner information in the results",
        default: false,
      });
    }
  }

  return parsed;
}

/**
 * Fetches campaign data for a customer.
 *
 * @param {{ customer: string }} params - Campaign request parameters.
 * @returns {Promise<Array>} Parsed campaigns list.
 */
export async function fetchCampaigns({ customer }) {
  if (!customer) return [];

  const response = await graphqlRequest({
    query: getCampaign,
    variables: {
      customer,
      status: "",
      contacts: [],
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const body = response?.data?.getCampaign?.body;
  if (typeof body !== "string") return [];

  const parsedBody = JSON.parse(body);
  return Array.isArray(parsedBody) ? parsedBody : [];
}

/**
 * Updates property hidden address flag.
 *
 * @param {object} variables - GraphQL mutation payload.
 * @returns {Promise<object>} Mutation data payload.
 */
export async function updatePropertyHiddenAddress(variables) {
  const response = await graphqlRequest({
    query: updateProperty,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const statusCode = response?.data?.updateProperty?.statusCode;
  if (statusCode !== "200") {
    throw new Error(`Failed to update hidden address status: statusCode: ${statusCode}`);
  }

  return response?.data?.updateProperty ?? {};
}

/**
 * Merges a patch into `property.additional_attributes` and calls `updateProperty` with the same
 * address identity fields as hidden-address updates. Used to persist `sow_s3_key` next to QMA keys.
 *
 * @param {object} property - Current property (must include id, customer, address fields)
 * @param {Record<string, unknown>} attributesPatch - Shallow patch for additional_attributes
 * @returns {Promise<object>} updateProperty payload
 */
export async function updatePropertyAdditionalAttributesMerge(property, attributesPatch) {
  if (!property?.id || !attributesPatch || typeof attributesPatch !== "object") {
    throw new Error("Invalid arguments for updatePropertyAdditionalAttributesMerge");
  }

  const existing =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};

  const merged = { ...existing, ...attributesPatch };

  if (Array.isArray(merged.photos)) {
    merged.photos = stripPhotoThumbnailsFromRecords(merged.photos);
  }

  const variables = {
    id: property.id,
    customer: property.customer || "",
    county: property.county || "",
    full_address: property.full_address || "",
    city: property.city || "",
    state: property.state || "",
    zipcode: property.zipcode || "",
    street_name: property.street_name || "",
    street_number: property.street_number || "",
    unit_number: property.unit_number || "",
    additional_attributes: JSON.stringify(merged),
    hidden_address: property.hidden_address,
  };

  const response = await graphqlRequest({
    query: updateProperty,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  if (!isSuccessStatusCode(response?.data?.updateProperty?.statusCode)) {
    throw new Error(`updateProperty failed: statusCode: ${response?.data?.updateProperty?.statusCode ?? "unknown"}`);
  }

  return response?.data?.updateProperty ?? {};
}

/**
 * Batch fetch properties for multiple customers.
 * SECURITY: Executes batch requests in parallel with error handling and data normalization.
 *
 * @param {Object} customerPropertyMap - Map of customer IDs to property ID arrays
 * @returns {Promise<Object>} Dictionary of properties keyed by property ID
 */
/**
 * Loads comp rows for QMA via getPropComps (same contract as legacy hook usage).
 *
 * @param {{ customerId: string, propertyId: string, compIds: string[] }} params
 * @returns {Promise<Array>} `comparisons` array or empty on failure
 */
export async function fetchPropComparisonsForQma({ customerId, propertyId, compIds }) {
  const cid = String(customerId ?? "").trim();
  const pid = String(propertyId ?? "").trim();
  if (!cid || !pid || !Array.isArray(compIds) || compIds.length === 0) return [];

  const response = await graphqlRequest({
    query: getPropComps,
    variables: {
      customer: cid,
      property_id: pid,
      comps: compIds,
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const payload = response?.data?.getPropComps;
  if (!isSuccessStatusCode(payload?.statusCode)) return [];

  try {
    const body = parseGraphqlBody(payload?.body);
    return Array.isArray(body?.comparisons) ? body.comparisons : [];
  } catch {
    return [];
  }
}

export async function batchFetchPropertiesByCustomer(customerPropertyMap) {
  if (!customerPropertyMap || typeof customerPropertyMap !== "object") {
    return {};
  }

  const allProperties = {};
  const batchPromises = [];

  Object.entries(customerPropertyMap).forEach(([customerId, propertyIds]) => {
    if (!customerId || typeof customerId !== "string") return;

    const sanitizedPropertyIds = Array.isArray(propertyIds)
      ? [
          ...new Set(
            propertyIds
              .filter((id) => id != null)
              .map(sanitizePropertyIdForBatch)
              .filter((id) => id != null && id !== "")
          ),
        ]
      : [];

    if (sanitizedPropertyIds.length === 0) return;

    const batchPromise = graphqlRequest({
      query: batchGetProperties,
      variables: {
        customer_id: customerId,
        property_ids: sanitizedPropertyIds,
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    })
      .then((response) => {
        const batchResult = response?.data?.batchGetProperties;
        if (!batchResult) return { customerId, properties: {} };

        const statusCode = batchResult.statusCode;
        const body = batchResult.body;

        if (isSuccessStatusCode(statusCode) && body) {
          try {
            const bodyString = extractBodyString(body);
            const parsedBody = JSON.parse(bodyString);
            const propertiesDict = {};

            const rawProps = parsedBody.properties || parsedBody.data || {};
            // Index by sanitized id so callers that sanitize deal.property_id
            // (e.g. fetchDealsWithProperties) can resolve rows. API keys often
            // keep the `property#` prefix.
            const indexProperty = (id, p, idx) => {
              if (!p) return;
              const normalized = normalizeProperty(p, idx);
              const rawKey = id != null ? String(id) : "";
              const sanitizedKey = sanitizePropertyIdForBatch(rawKey || p.id);
              if (sanitizedKey) propertiesDict[sanitizedKey] = normalized;
              if (rawKey && rawKey !== sanitizedKey) propertiesDict[rawKey] = normalized;
            };
            if (rawProps && typeof rawProps === "object" && !Array.isArray(rawProps)) {
              Object.entries(rawProps).forEach(([id, p], idx) => {
                indexProperty(id, p, idx);
              });
            } else if (Array.isArray(rawProps)) {
              rawProps.forEach((p, idx) => {
                if (p?.id) indexProperty(p.id, p, idx);
              });
            }

            return { customerId, properties: propertiesDict };
          } catch (e) {
            if (IS_DEV) console.error("[propertyApi] Failed to parse batch body", e);
            return { customerId, properties: {} };
          }
        }
        return { customerId, properties: {} };
      })
      .catch((err) => {
        if (IS_DEV) console.error("[propertyApi] Batch fetch failed", err);
        return { customerId, properties: {} };
      });

    batchPromises.push(batchPromise);
  });

  try {
    const results = await Promise.all(batchPromises);
    results.forEach((result) => {
      if (result.properties && Object.keys(result.properties).length > 0) {
        Object.assign(allProperties, result.properties);
      }
    });
    return allProperties;
  } catch (err) {
    if (IS_DEV) console.error("[propertyApi] batchFetchPropertiesByCustomer failed", err);
    return {};
  }
}

/** Activation state values — mirrors PropertyActivationStatus backend enum. */
export const ACTIVATION_STATE = {
  ACTIVE: "ACTIVE",
  DEACTIVATING: "DEACTIVATING",
  DEACTIVATED: "DEACTIVATED",
};

const REMOVAL_SUBSCRIPTION_TIMEOUT_MS = 120_000;

/**
 * Whether a property should be treated as active in the UI.
 *
 * @param {object | null | undefined} property
 * @returns {boolean}
 */
export function isPropertyActiveState(property) {
  if (!property) return false;
  if (property.active === false) return false;
  const activationState = property.activation_state;
  if (activationState === ACTIVATION_STATE.DEACTIVATED || activationState === ACTIVATION_STATE.DEACTIVATING) return false;
  return true;
}

/**
 * @param {object | null | undefined} property
 * @returns {boolean}
 */
export function isPropertyDeactivatingState(property) {
  return property?.activation_state === ACTIVATION_STATE.DEACTIVATING;
}

/**
 * Whether property detail should render main content (vs full unavailable screen).
 *
 * @param {object | null | undefined} property
 * @returns {boolean}
 */
export function isPropertyDetailViewable(property) {
  if (!property) return false;
  if (property.active === false) return false;
  return property.activation_state !== ACTIVATION_STATE.DEACTIVATED;
}

/**
 * @param {object} property
 * @param {Record<string, unknown>} [overrides]
 * @returns {Record<string, unknown>}
 */
export function buildUpdatePropertyVariables(property, overrides = {}) {
  const existing =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};

  return {
    id: property.id,
    customer: property.customer || "",
    county: property.county || "",
    full_address: property.full_address || "",
    city: property.city || "",
    state: property.state || "",
    zipcode: property.zipcode || "",
    street_name: property.street_name || "",
    street_number: property.street_number || "",
    unit_number: property.unit_number || "",
    hidden_address: property.hidden_address,
    additional_attributes: JSON.stringify(existing),
    ...overrides,
  };
}

/**
 * @param {unknown} body
 * @returns {string}
 */
function messageFromRemovalBody(body) {
  const parsed = parseGraphqlBody(body);
  if (parsed == null) return "";
  if (typeof parsed === "string") return parsed.trim();
  if (typeof parsed !== "object") return "";
  if (typeof parsed.message === "string" && parsed.message.trim()) return parsed.message.trim();
  if (typeof parsed.error === "string" && parsed.error.trim()) return parsed.error.trim();
  return "";
}

/**
 * Deactivates a property via removeProperty + async cascade subscription.
 *
 * @param {{
 *   customer: string,
 *   propertyId: string,
 *   userEmail: string,
 *   subscriptionId: string,
 *   onAccepted?: () => void,
 * }} params
 * @returns {Promise<{ property_id?: string, activation_state?: string }>}
 */
export async function deactivateProperty({ customer, propertyId, userEmail, subscriptionId, onAccepted }) {
  const cid = String(customer ?? "").trim();
  const pid = String(propertyId ?? "").trim();
  const email = String(userEmail ?? "").trim();
  const subId = String(subscriptionId ?? "").trim();

  if (!cid || !pid || !email || !subId) {
    throw new Error("Missing required fields for property deactivation");
  }

  return new Promise((resolve, reject) => {
    /** @type {import('rxjs').Subscription | null} */
    let subscription = null;
    let settled = false;
    let accepted = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      subscription?.unsubscribe();
      fn(value);
    };

    const timeoutId = setTimeout(() => {
      finish(
        reject,
        Object.assign(new Error("Property deactivation timed out. Please refresh and try again."), {
          timedOutAfterAccept: accepted,
        })
      );
    }, REMOVAL_SUBSCRIPTION_TIMEOUT_MS);

    try {
      subscription = graphqlRequest({
        query: onPropertyRemovalResponse,
        variables: { id: subId },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      }).subscribe({
        next: (response) => {
          const data = response?.data?.onPropertyRemovalResponse;
          if (!data) return;

          const statusCode = String(data.statusCode ?? "");
          if (statusCode === "200" && data.status === "done") {
            const body = parseGraphqlBody(data.body);
            finish(resolve, body && typeof body === "object" ? body : { property_id: pid });
            return;
          }

          const message = messageFromRemovalBody(data.body) || "Property deactivation failed";
          finish(reject, Object.assign(new Error(message), { statusCode: Number(statusCode) || statusCode }));
        },
        error: (err) => {
          finish(reject, err instanceof Error ? err : new Error(String(err?.message || err)));
        },
      });
    } catch (err) {
      finish(reject, err instanceof Error ? err : new Error(String(err)));
      return;
    }

    graphqlRequest({
      query: removeProperty,
      variables: {
        customer: cid,
        property_id: pid,
        user_email: email,
        subscription_id: subId,
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    })
      .then((response) => {
        const payload = response?.data?.removeProperty;
        const statusCode = String(payload?.statusCode ?? "");
        if (statusCode === "200") {
          accepted = true;
          // Guard: subscription may have resolved before this .then handler runs.
          // If settled, the caller's post-await code already applied DEACTIVATED.
          if (!settled) {
            try {
              onAccepted?.();
            } catch {
              // Caller patch failures must not reject an accepted deactivation.
            }
          }
          return;
        }

        const message = messageFromRemovalBody(payload?.body) || `Property deactivation failed (${statusCode})`;
        finish(reject, Object.assign(new Error(message), { statusCode: Number(statusCode) || statusCode }));
      })
      .catch((err) => {
        finish(reject, err instanceof Error ? err : new Error(String(err)));
      });
  });
}

/**
 * Reactivates a property by setting active=true.
 *
 * @param {{ property: object }} params
 * @returns {Promise<object>}
 */
export async function reactivateProperty({ property }) {
  if (!property?.id) throw new Error("Property is required for reactivation");

  const variables = buildUpdatePropertyVariables(property, {
    active: true,
  });

  const response = await graphqlRequest({
    query: updateProperty,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  if (!isSuccessStatusCode(response?.data?.updateProperty?.statusCode)) {
    throw new Error(`updateProperty failed: statusCode: ${response?.data?.updateProperty?.statusCode ?? "unknown"}`);
  }

  return response?.data?.updateProperty ?? {};
}
